import { useCallback, useEffect, useRef, useState } from "react";
import { fetchTariffRequests, type UserTariffRequest } from "../api/user";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";

interface UseTariffRequestListOptions {
  page: number;
  pageSize: number;
  enabled?: boolean;
}

export const useTariffRequestList = ({ page, pageSize, enabled = true }: UseTariffRequestListOptions) => {
  const [data, setData] = useState<UserTariffRequest[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const listParamsRef = useRef({ page, pageSize });
  const hasLoadedOnceRef = useRef(false);

  const refetch = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const removeByPayId = useCallback((payId: string) => {
    setData((current) => current.filter((item) => item.payId !== payId));
    setTotalElements((current) => Math.max(0, current - 1));
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const paginationChanged =
      listParamsRef.current.page !== page || listParamsRef.current.pageSize !== pageSize;
    listParamsRef.current = { page, pageSize };

    let cancelled = false;
    const showFullLoading = paginationChanged || !hasLoadedOnceRef.current;

    if (showFullLoading) {
      setLoading(true);
    }
    setError(null);

    fetchTariffRequests({ page, pageSize })
      .then((response) => {
        if (!cancelled) {
          setData(response.items);
          setTotalPages(Math.max(1, response.totalPages));
          setTotalElements(response.totalElements);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err, "Не удалось загрузить запросы на тарифы"));
          setData([]);
          setTotalPages(1);
          setTotalElements(0);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          hasLoadedOnceRef.current = true;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, enabled, reloadToken]);

  return { data, totalPages, totalElements, loading, error, refetch, removeByPayId };
};
